import { Button, ButtonGroup, HStack } from "@chakra-ui/react";
import { Select, SingleValue } from "chakra-react-select";
import React, { FunctionComponent, ReactElement } from "react";

// Types
import { PaginationResponse } from "../../../utils/types";

interface DataTablePaginationProps {
	isDisabled: boolean | undefined;
	meta: PaginationResponse;
	currentPage: number;
	limit: number;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Define a set of actions relevant to the pagination actions
 */
enum PaginationActions {
	FirstPage,
	LastPage,
	PreviousPage,
	NextPage
}

// Define some types so we're not hard-coding shapes all over the place
// Might need to move this to global types file after adjusting names
type ChakraReactSelectOption = { label: string; value: string };
type SingleSelectOption = SingleValue<ChakraReactSelectOption>;

/**
 * Handles Pagination features, including First, Last, Next, and Previous page buttons
 * as well as disabling them based on current/max pages. Also handles skipping to a given
 * page from the Select element
 * @param props typeof: DataTablePaginationProps
 */
const DataTablePagination: FunctionComponent<DataTablePaginationProps> = (
	props: DataTablePaginationProps
): ReactElement => {
	const { isDisabled, meta, currentPage, limit, setCurrentPage } = props;

	/**
	 * Helper function to determine maximum page amount given current page size
	 * @returns Maximum amount of pages given the total result count and page size
	 */
	const getMaxPages = (): number => meta.last_page;

	/**
	 * Event handler to manage moving between pages using the navigation buttons
	 * @param page The number of the page to skip to, not the 0-based index
	 */
	const handleGoToPage = (page: number): void => setCurrentPage(page);

	/**
	 * Event handler to manage skipping to a given page via the Select option
	 * @param newValue typeof: SingleValue<{ label: string; value: string }>
	 */
	const handlePaginationSelectChange = (newValue: SingleSelectOption): void =>
		setCurrentPage(parseInt(newValue?.value ?? "-1"));

	/**
	 * Helper function to programmatically create navigation buttons for individual pages
	 * @returns typeof: Array<ReactElement>, an array of size N buttons, where N is max page count
	 */
	const getPages = (): Array<ReactElement> => {
		const pagesList: Array<ReactElement> = [];
		const minNavigablePage: number = currentPage - 5 < 0 ? 1 : currentPage - 5;
		const maxNavigablePage: number = currentPage + 5 > meta.last_page ? meta.last_page : currentPage + 5;

		for (let i = minNavigablePage; i <= maxNavigablePage; i++) {
			pagesList.push(
				<Button
					key={`page-btn-${i}-of-${meta.total}`}
					variant="solid"
					colorScheme={i === currentPage ? "orange" : "gray"}
					backgroundColor={i === currentPage ? "" : "white"}
					isDisabled={isDisabled}
					onClick={() => handleGoToPage(i)}>
					{i}
				</Button>
			);
		}

		return pagesList;
	};

	/**
	 * Helper function to programmatically create Chakra React Select options for Select navigation
	 * between pages
	 * @returns typeof: Array<ChakraReactSelectOption>, an array of Select options
	 */
	const getPageOptions = (): Array<ChakraReactSelectOption> => {
		const pageList: Array<ChakraReactSelectOption> = [];
		for (let i = 0; i < getMaxPages(); i++) {
			pageList.push({
				label: (i + 1).toString(),
				value: (i + 1).toString()
			});
		}

		return pageList;
	};

	/**
	 * Event Handler to facilitate navigation bewteen pages using First/Next/Previous/Last buttons
	 * @param action typeof: PaginationActions
	 */
	const handlePaginationAction = (action: PaginationActions): void => {
		switch (action) {
			default:
			case PaginationActions.FirstPage:
				setCurrentPage(1);
				break;
			case PaginationActions.LastPage:
				setCurrentPage(getMaxPages());
				break;
			case PaginationActions.NextPage:
				// Is there a next page?
				if (currentPage + 1 <= getMaxPages()) setCurrentPage(currentPage + 1);
				break;
			case PaginationActions.PreviousPage:
				// Is there a previous page?
				if (currentPage - 1 > 0) setCurrentPage(currentPage - 1);
				break;
		}
	};

	// Series of simple helper functions to enable/disable buttons
	const isFirstPageDisabled = (): boolean => currentPage === 1;
	const isPreviousPageDisabled = (): boolean => currentPage - 1 <= 0;
	const isNextPageDisabled = (): boolean => currentPage + 1 > getMaxPages();
	const isLastPageDisabled = (): boolean => currentPage === getMaxPages();

	return (
		<div style={{ backgroundColor: "white", maxWidth: "100%" }}>
			<ButtonGroup justifyContent="center" display="flex" mt={5} pt={"10px"} id={"id"}>
				<Button
					colorScheme="gray"
					isDisabled={isDisabled || isFirstPageDisabled()}
					onClick={() => handlePaginationAction(PaginationActions.FirstPage)}>
					First Page
				</Button>
				<Button
					colorScheme="gray"
					isDisabled={isDisabled || isPreviousPageDisabled()}
					onClick={() => handlePaginationAction(PaginationActions.PreviousPage)}>
					Previous Page
				</Button>
				{getPages()}
				<Button
					colorScheme="gray"
					isDisabled={isDisabled || isNextPageDisabled()}
					onClick={() => handlePaginationAction(PaginationActions.NextPage)}>
					Next Page
				</Button>
				<Button
					colorScheme="gray"
					isDisabled={isDisabled || isLastPageDisabled()}
					onClick={() => handlePaginationAction(PaginationActions.LastPage)}>
					Last Page
				</Button>
			</ButtonGroup>
			<HStack justifyContent="center" mt={3} pb={3}>
				<HStack>
					<span>Page&nbsp;</span>
					<Select
						options={getPageOptions()}
						menuPlacement={"top"}
						value={[{ label: currentPage.toString(), value: currentPage.toString() }]}
						closeMenuOnSelect={true}
						isDisabled={isDisabled}
						onChange={handlePaginationSelectChange}
						size="sm"
					/>
					<span>&nbsp;of {getMaxPages()}</span>
					<span style={{ marginLeft: "25px" }}>{meta.total} Total Rows</span>
				</HStack>
			</HStack>
		</div>
	);
};

export default DataTablePagination;
